/**
 * Format utilities for common data formatting needs.
 */

/**
 * Format a number of bytes into a human-readable string.
 * 
 * @param {number} bytes - The number of bytes
 * @param {number} decimals - Number of decimal places (default: 2)
 * @returns {string} Formatted string like "1.5 KB" or "2.3 MB"
 * 
 * @example
 * formatBytes(1024) // "1 KB"
 * formatBytes(1234567) // "1.18 MB"
 * formatBytes(0) // "0 Bytes"
 */
export function formatBytes(bytes, decimals = 2) {
    if (!bytes || bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB'];
    
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
}

/**
 * Format a number with thousand separators.
 * 
 * @param {number} num - The number to format
 * @returns {string} Formatted number string
 * 
 * @example
 * formatNumber(1234567) // "1,234,567"
 */
export function formatNumber(num) {
    if (num === null || num === undefined) return '';
    return num.toLocaleString();
}

/**
 * Format a percentage value.
 * 
 * @param {number} value - The decimal value (e.g., 0.75 for 75%)
 * @param {number} decimals - Number of decimal places (default: 1)
 * @returns {string} Formatted percentage string
 * 
 * @example
 * formatPercent(0.756) // "75.6%"
 */
export function formatPercent(value, decimals = 1) {
    if (value === null || value === undefined) return '';
    return `${(value * 100).toFixed(decimals)}%`;
}

/**
 * Truncate a string to a maximum length with ellipsis.
 * 
 * @param {string} str - The string to truncate
 * @param {number} maxLength - Maximum length (default: 50)
 * @returns {string} Truncated string with ellipsis if needed
 * 
 * @example
 * truncateString("This is a very long string", 10) // "This is a..."
 */
export function truncateString(str, maxLength = 50) {
    if (!str) return '';
    if (str.length <= maxLength) return str;
    return str.substring(0, maxLength - 3) + '...';
}
